import { Scope } from "@nijiurachan/js/react/PreactWrapperV1";
import { useEffect, useRef, useState } from "react";
import { AttachmentPreviewList } from "@/features/otegaki-upfile/components/AttachmentPreviewList";
import type { UpfileTrailingAction } from "@/features/otegaki-upfile/components/UpfileActions";
import { UpfileInput } from "@/features/otegaki-upfile/components/UpfileInput";
import {
  useUpfileHost,
  useUpfileState,
} from "@/features/otegaki-upfile/hooks/useUpfile";
import {
  type AttachmentSelectionMode,
  appendAttachmentFiles,
  REPLY_ATTACHMENT_MAX_COUNT,
  removeAttachmentFile,
  THREAD_ATTACHMENT_COUNT,
} from "@/features/otegaki-upfile/lib/attachmentSelection";

interface Props {
  scopeName: string;
  fullKey: string;
  mode: AttachmentSelectionMode;
  files: readonly File[];
  onChange: (files: File[]) => void;
  allowImage: boolean;
  disabled?: boolean;
  onPaintPopupChange?: (isOpen: boolean) => void;
  surface?: "desktop" | "mobile";
  maxCount?: number;
  statusMessage?: string | null;
  trailingAction?: UpfileTrailingAction;
  actionExpandedContent?: React.ReactNode;
}

export const AttachmentPicker: React.FunctionComponent<Props> = ({
  scopeName,
  fullKey,
  mode,
  files,
  onChange,
  allowImage,
  disabled = false,
  onPaintPopupChange,
  surface = "mobile",
  maxCount: maxCountOverride,
  statusMessage = null,
  trailingAction,
  actionExpandedContent,
}: Props) => {
  const modeMaxCount =
    mode === "thread" ? THREAD_ATTACHMENT_COUNT : REPLY_ATTACHMENT_MAX_COUNT;
  const maxCount = Math.min(maxCountOverride ?? modeMaxCount, modeMaxCount);
  const host = useUpfileHost(fullKey);
  const state = useUpfileState(fullKey);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const filesRef = useRef(files);
  const isPaintPopupOpen = Boolean(state?.isAxnosOpen || state?.isKlecksOpen);

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    onPaintPopupChange?.(isPaintPopupOpen);
  }, [isPaintPopupOpen, onPaintPopupChange]);

  useEffect(() => {
    // Keep the embedded input mounted and untouched while prepare-submit is
    // converting Hacchan/canvas data. The form reads that pending File
    // directly, and a failed submit is captured here after it becomes idle.
    if (disabled || !host || !state?.hasSelectedFile) return;
    const file = host.querySelector<HTMLInputElement>(
      'input[type="file"][name="upfile"]',
    )?.files?.[0];
    if (!file) return;

    const currentFiles = filesRef.current;
    if (currentFiles.length >= maxCount) {
      setSelectionError(`添付ファイルは${maxCount}個までです。`);
      host.clickClear();
      return;
    }
    const result = appendAttachmentFiles(mode, currentFiles, [file]);
    const selectionError =
      result.files.length > maxCount
        ? `添付ファイルは${maxCount}個までです。`
        : result.error;
    setSelectionError(selectionError);
    if (!selectionError) {
      filesRef.current = result.files;
      onChange(result.files);
    }
    host.clickClear();
  }, [disabled, host, maxCount, mode, onChange, state?.hasSelectedFile]);

  const handleRemove = (index: number): void => {
    setSelectionError(null);
    const nextFiles = removeAttachmentFile(filesRef.current, index);
    filesRef.current = nextFiles;
    onChange(nextFiles);
  };

  return (
    <fieldset className="m-0 space-y-2 border-0 p-0">
      <legend className="sr-only">添付ファイル</legend>
      <AttachmentPreviewList
        files={files}
        onRemove={handleRemove}
        disabled={disabled}
        surface={surface}
      />
      {(files.length < maxCount || trailingAction) && (
        <div
          className={disabled ? "pointer-events-none opacity-60" : undefined}
          aria-disabled={disabled || undefined}
        >
          <Scope name={scopeName}>
            <UpfileInput
              fullKey={fullKey}
              allowImage={allowImage}
              attachmentsDisabled={files.length >= maxCount}
              surface={surface}
              trailingAction={trailingAction}
            />
          </Scope>
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        {mode === "thread"
          ? "添付ファイルを1個選択してください。"
          : `添付ファイルは最大${maxCount}個まで。選択順に投稿されます。`}
      </p>
      {statusMessage && (
        <output className="block text-xs text-muted-foreground">
          {statusMessage}
        </output>
      )}
      {selectionError && (
        <p className="text-sm text-destructive" role="alert">
          {selectionError}
        </p>
      )}
      {actionExpandedContent}
    </fieldset>
  );
};
